import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getSalesMessages, replySalesMessage, getSalesReplies, updateSalesStatus } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Envelope,
  EnvelopeOpen,
  PaperPlaneRight,
  Check,
  Clock,
  CaretRight,
  User
} from '@phosphor-icons/react';

export default function SalesInboxPage() {
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await getSalesMessages();
      setMessages(res.data.messages);
    } catch (e) { toast.error('Failed to load messages'); }
    finally { setLoading(false); }
  };

  const handleSelect = async (msg) => {
    setSelected(msg);
    setReplyText('');
    // Mark as read
    if (msg.status === 'new') {
      await updateSalesStatus(msg.msg_id, { status: 'read' }).catch(() => {});
      setMessages(prev => prev.map(m => m.msg_id === msg.msg_id ? { ...m, status: 'read' } : m));
    }
    // Fetch replies
    try {
      const res = await getSalesReplies(msg.msg_id);
      setReplies(res.data.replies);
    } catch { setReplies([]); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      await replySalesMessage(selected.msg_id, { reply: replyText });
      toast.success('Reply sent');
      setReplyText('');
      setSelected({ ...selected, replied: true, status: 'replied' });
      setMessages(prev => prev.map(m => m.msg_id === selected.msg_id ? { ...m, replied: true, status: 'replied' } : m));
      const res = await getSalesReplies(selected.msg_id);
      setReplies(res.data.replies);
    } catch (e) { toast.error('Failed to send reply'); }
    finally { setSending(false); }
  };

  const unread = messages.filter(m => m.status === 'new').length;

  if (loading) {
    return (<Layout title="Sales Inbox"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div></div></Layout>);
  }

  return (
    <Layout title="Sales Inbox">
      <div data-testid="sales-inbox-page" className="h-[calc(100vh-12rem)]">
        <div className="grid grid-cols-3 gap-6 h-full">
          {/* Message List */}
          <div className="col-span-1 bg-white border border-zinc-200 rounded-sm overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <p className="text-xs uppercase tracking-widest text-zinc-500">{messages.length} Messages</p>
              {unread > 0 && (
                <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded-full font-mono" data-testid="unread-badge">{unread}</span>
              )}
            </div>
            <div className="flex-1 overflow-auto">
              {messages.length === 0 ? (
                <div className="p-8 text-center text-zinc-400">
                  <Envelope size={32} className="mx-auto mb-2" />
                  <p className="text-sm">No messages yet</p>
                </div>
              ) : messages.map((msg) => (
                <button
                  key={msg.msg_id}
                  onClick={() => handleSelect(msg)}
                  data-testid={`msg-${msg.msg_id}`}
                  className={`w-full text-left px-4 py-3 border-b border-zinc-100 transition-colors ${
                    selected?.msg_id === msg.msg_id ? 'bg-zinc-100 border-l-4 border-l-zinc-950' : 'hover:bg-zinc-50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-1">
                      {msg.status === 'new' ? (
                        <Envelope size={14} weight="fill" className="text-blue-500" />
                      ) : msg.replied ? (
                        <Check size={14} className="text-emerald-500" />
                      ) : (
                        <EnvelopeOpen size={14} className="text-zinc-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm truncate ${msg.status === 'new' ? 'font-semibold' : ''}`}>
                          {msg.name || msg.email || 'Anonymous'}
                        </p>
                        <span className="text-[10px] text-zinc-400 flex-shrink-0 ml-2">
                          {new Date(msg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 truncate">{msg.message}</p>
                    </div>
                    <CaretRight size={12} className="text-zinc-300 mt-1 flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Detail Panel */}
          <div className="col-span-2 bg-white border border-zinc-200 rounded-sm overflow-hidden flex flex-col">
            {selected ? (
              <>
                {/* Header */}
                <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-zinc-200 rounded-full flex items-center justify-center">
                        <User size={18} className="text-zinc-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{selected.name || 'Anonymous'}</h3>
                        <p className="text-xs text-zinc-500">{selected.email || 'No email provided'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs uppercase tracking-wider border rounded-sm ${
                        selected.status === 'new' ? 'bg-blue-50 text-blue-700 border-blue-500' :
                        selected.status === 'replied' ? 'bg-emerald-50 text-emerald-700 border-emerald-500' :
                        'bg-zinc-100 text-zinc-600 border-zinc-300'
                      }`}>{selected.status}</span>
                      <span className="text-xs text-zinc-400">
                        {new Date(selected.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Messages Thread */}
                <div className="flex-1 overflow-auto p-6 space-y-4">
                  {/* Original message */}
                  <div className="flex justify-start">
                    <div className="max-w-[80%] bg-zinc-100 border border-zinc-200 rounded-sm px-4 py-3">
                      <p className="text-xs text-zinc-500 mb-1">{selected.name || 'User'} - {new Date(selected.created_at).toLocaleString()}</p>
                      <p className="text-sm leading-relaxed">{selected.message}</p>
                    </div>
                  </div>

                  {/* Replies */}
                  {replies.map((reply) => (
                    <div key={reply.reply_id} className="flex justify-end">
                      <div className="max-w-[80%] bg-zinc-950 text-white rounded-sm px-4 py-3">
                        <p className="text-xs text-zinc-400 mb-1">{reply.replied_by_name} - {new Date(reply.created_at).toLocaleString()}</p>
                        <p className="text-sm leading-relaxed">{reply.reply}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply Input */}
                <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50">
                  <div className="flex gap-3">
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Write your reply..."
                      className="rounded-sm resize-none flex-1"
                      rows={2}
                      data-testid="reply-textarea"
                    />
                    <Button onClick={handleReply} disabled={sending || !replyText.trim()} className="bg-zinc-950 text-white rounded-sm self-end" data-testid="send-reply-button">
                      <PaperPlaneRight size={16} className="mr-2" />
                      {sending ? 'Sending...' : 'Reply'}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-zinc-500">
                <div className="text-center">
                  <Envelope size={48} className="mx-auto mb-4 text-zinc-300" />
                  <p className="font-medium">Select a message to view</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
